/**
 * Modal de détails d'événement
 * Affiche les informations complètes d'un événement ou réservation
 */

import { CalendarEventData } from '@/hooks/useCalendarEvents';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar1, Document, Location, People, User } from 'iconsax-react';
import { X } from 'lucide-react';

interface EventDetailsModalProps {
  event: CalendarEventData | null;
  onClose: () => void;
}

export function EventDetailsModal({ event, onClose }: EventDetailsModalProps) {
  if (!event) return null;

  const isBooking = event.type === 'booking';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 48, 60, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '20px',
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 48, 60, 0.3)',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header avec gradient */}
        <div
          style={{
            background: `linear-gradient(135deg, ${event.color || '#00758d'} 0%, ${event.accentColor || '#8E0B56'} 100%)`,
            padding: '32px',
            borderRadius: '20px 20px 0 0',
            position: 'relative',
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            }}
          >
            <X style={{ width: '20px', height: '20px', color: 'white' }} />
          </button>

          <div
            style={{
              display: 'inline-flex',
              padding: '6px 12px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(8px)',
              fontSize: '12px',
              fontWeight: 600,
              color: 'white',
              marginBottom: '12px',
            }}
          >
            {isBooking ? 'Réservation' : 'Événement'}
          </div>

          <h2
            style={{
              fontSize: '24px',
              fontWeight: 800,
              color: 'white',
              marginBottom: '8px',
              lineHeight: 1.3,
            }}
          >
            {event.title}
          </h2>

          {event.spaceName && (
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>{event.spaceName}</p>
          )}
        </div>

        {/* Contenu */}
        <div style={{ padding: '32px' }}>
          {/* Date et heure */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(0, 117, 141, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Calendar1 variant="Bold" style={{ width: '20px', height: '20px' }} color="#00758d" />
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#00303c' }}>
                  {format(event.start, 'EEEE d MMMM yyyy', { locale: fr })}
                </div>
                <div style={{ fontSize: '13px', color: 'rgba(0,48,60,0.6)' }}>
                  {event.allDay
                    ? 'Toute la journée'
                    : `${format(event.start, 'HH:mm', { locale: fr })} - ${format(event.end, 'HH:mm', { locale: fr })}`}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#00303c',
                  marginBottom: '8px',
                }}
              >
                Description
              </h3>
              <p style={{ fontSize: '14px', color: 'rgba(0,48,60,0.7)', lineHeight: 1.6 }}>{event.description}</p>
            </div>
          )}

          {/* Informations supplémentaires */}
          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Organisateur/Client */}
            {(event.organizerName || event.customerName) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <User variant="Bulk" style={{ width: '20px', height: '20px' }} color="#00758d" />
                <div>
                  <div style={{ fontSize: '12px', color: 'rgba(0,48,60,0.6)', fontWeight: 600 }}>
                    {isBooking ? 'Client' : 'Organisateur'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#00303c', fontWeight: 600 }}>
                    {event.organizerName || event.customerName}
                  </div>
                </div>
              </div>
            )}

            {/* Lieu */}
            {event.location && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Location variant="Bulk" style={{ width: '20px', height: '20px' }} color="#00758d" />
                <div>
                  <div style={{ fontSize: '12px', color: 'rgba(0,48,60,0.6)', fontWeight: 600 }}>Lieu</div>
                  <div style={{ fontSize: '14px', color: '#00303c', fontWeight: 600 }}>{event.location}</div>
                </div>
              </div>
            )}

            {/* Capacité/Participants */}
            {(event.capacity || event.participantCount) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <People variant="Bulk" style={{ width: '20px', height: '20px' }} color="#00758d" />
                <div>
                  <div style={{ fontSize: '12px', color: 'rgba(0,48,60,0.6)', fontWeight: 600 }}>
                    {isBooking ? 'Participants' : 'Capacité'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#00303c', fontWeight: 600 }}>
                    {event.participantCount || event.capacity} personne(s)
                    {event.registeredCount !== undefined &&
                      ` (${event.registeredCount} inscrit${event.registeredCount > 1 ? 's' : ''})`}
                  </div>
                </div>
              </div>
            )}

            {/* Référence réservation */}
            {event.reference && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Document variant="Bulk" style={{ width: '20px', height: '20px' }} color="#00758d" />
                <div>
                  <div style={{ fontSize: '12px', color: 'rgba(0,48,60,0.6)', fontWeight: 600 }}>Référence</div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#00303c',
                      fontWeight: 600,
                      fontFamily: 'monospace',
                    }}
                  >
                    {event.reference}
                  </div>
                </div>
              </div>
            )}

            {/* Statut réservation */}
            {event.bookingStatus && (
              <div
                style={{
                  marginTop: '8px',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  background: '#f8f9fa',
                  border: '2px solid #e9ecef',
                }}
              >
                <div style={{ fontSize: '12px', color: 'rgba(0,48,60,0.6)', marginBottom: '4px' }}>Statut</div>
                <div
                  style={{
                    display: 'inline-flex',
                    padding: '4px 12px',
                    borderRadius: '8px',
                    background: getStatusColor(event.bookingStatus).bg,
                    color: getStatusColor(event.bookingStatus).text,
                    fontSize: '13px',
                    fontWeight: 700,
                  }}
                >
                  {getStatusLabel(event.bookingStatus)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helpers pour les statuts
function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    PENDING: 'En attente',
    PAYMENT_PENDING: 'Paiement en attente',
    PAID: 'Payé',
    CONFIRMED: 'Confirmé',
    QUOTE_REQUESTED: 'Devis demandé',
    QUOTED: 'Devis envoyé',
    APPROVED: 'Approuvé',
    REJECTED: 'Rejeté',
    CANCELLED: 'Annulé',
    COMPLETED: 'Terminé',
  };
  return labels[status] || status;
}

function getStatusColor(status: string): { bg: string; text: string } {
  // Couleurs SING : Turquoise #00758d, Jaune #dfc32f, Magenta #a4006c, Marron #5c4738
  const colors: Record<string, { bg: string; text: string }> = {
    PENDING: { bg: 'rgba(223, 195, 47, 0.15)', text: '#dfc32f' }, // Jaune SING
    PAYMENT_PENDING: { bg: 'rgba(223, 195, 47, 0.15)', text: '#dfc32f' }, // Jaune SING
    PAID: { bg: 'rgba(0, 117, 141, 0.15)', text: '#00758d' }, // Turquoise SING
    CONFIRMED: { bg: 'rgba(0, 117, 141, 0.15)', text: '#00758d' }, // Turquoise SING
    QUOTE_REQUESTED: { bg: 'rgba(223, 195, 47, 0.15)', text: '#dfc32f' }, // Jaune SING
    QUOTED: { bg: 'rgba(164, 0, 108, 0.15)', text: '#a4006c' }, // Magenta SING
    APPROVED: { bg: 'rgba(0, 117, 141, 0.15)', text: '#00758d' }, // Turquoise SING
    REJECTED: { bg: 'rgba(92, 71, 56, 0.15)', text: '#5c4738' }, // Marron SING
    CANCELLED: { bg: 'rgba(92, 71, 56, 0.15)', text: '#5c4738' }, // Marron SING
    COMPLETED: { bg: 'rgba(0, 117, 141, 0.15)', text: '#00758d' }, // Turquoise SING
  };
  return colors[status] || { bg: 'rgba(0, 117, 141, 0.15)', text: '#00758d' };
}
